import { useEffect, useRef } from 'react';
import {
  BarChart3,
  Bot,
  Code2,
  Headphones,
  Ticket,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';

/**
 * Login-screen visualization: a glass globe with the platform's own concepts —
 * tickets, webhooks, the API, AI, customers, agents, automation and analytics —
 * in orbit around it. Every colour comes from the app's primary/slate palette,
 * and the theme flag is read once per frame so it follows the theme toggle.
 *
 * Three things are worth knowing before editing this file.
 *
 * 1. Everything on the sphere is precomputed as unit vectors at module load:
 *    the dot matrix, the graticule and the surface mesh. None of that geometry
 *    changes as the globe turns, so each frame only applies one shared rotation
 *    to cached vectors. That is what keeps several thousand points per frame
 *    cheap.
 *
 * 2. The globe is hollow: the far wall is drawn dimmed, then a faint interior
 *    haze, then the near wall — so the continents on the back show through.
 *    Continents are filled bands of latitude with an outline traced over
 *    them, both derived from the same interpolated land mask. Depth is carried by dot size
 *    rather than alpha, so the whole grid needs a single fillStyle, and dots
 *    are drawn as squares: at this size they read the same as circles and cost
 *    a fraction as much. Coastlines come from marching squares over the
 *    bilinearly interpolated mask, which is what makes them curve.
 *
 * 3. Orbits are genuinely three-dimensional. Each ring is a circle in 3D,
 *    inclined and swivelled, then projected. Rings are drawn in two passes —
 *    the half behind the globe before the sphere, the half in front after it —
 *    and the HTML nodes riding them are positioned per frame from the same
 *    maths, scaled by depth and hidden when the globe occludes them.
 */

const BOX = 1000;
const C = BOX / 2;
const GLOBE_R = 300;

/** Degrees the globe turns per second. One rotation ≈ 60s. */
const SPIN_PER_SEC = 6;
/** Axial tilt, so the poles aren't dead-on and the sphere reads as a body. */
const AXIAL_TILT = -16;

/** Nodes in the surface mesh, and how close two must be to be linked (radians). */
const MESH_NODES = 150;
const MESH_LINK_ARC = 0.34;

const RAD = Math.PI / 180;

type Vec = { x: number; y: number; z: number };

/** Lat/lon on the unit sphere, in the globe's own (unrotated) frame. */
const unit = (lat: number, lon: number): Vec => {
  const la = lat * RAD;
  const lo = lon * RAD;
  return {
    x: Math.cos(la) * Math.sin(lo),
    y: -Math.sin(la),
    z: Math.cos(la) * Math.cos(lo),
  };
};

/** Path to the Natural Earth 110m data, fetched after mount rather than bundled. */
const WORLD_URL = '/geo/world-110m.json';

type Quad = [Vec, Vec, Vec, Vec];

/**
 * Shape of the file produced by .geotmp/build-world.mjs.
 *  lines  every boundary arc once — coastlines and internal borders alike,
 *         as flat [lon, lat, lon, lat, …]
 *  bands  land fill already scanlined into longitude spans per latitude band
 */
type World = {
  band: number;
  lines: number[][];
  bands: Array<[number, number[]]>;
};

/** Boundary arcs as polylines of unit vectors — projection is all that is left
 *  to do per frame. */
const buildOutlines = (world: World): Vec[][] =>
  world.lines.map((flat) => {
    const line: Vec[] = [];
    for (let i = 0; i + 1 < flat.length; i += 2) line.push(unit(flat[i + 1], flat[i]));
    return line;
  });

/**
 * Land fill as quads, from the precomputed spans. Bands overlap slightly so
 * anti-aliasing cannot leave hairlines where they meet, and each span is
 * subdivided in longitude so its edges still follow the sphere's curvature.
 */
const buildLandQuads = (world: World): Quad[] => {
  const overlap = 0.18;
  const maxSpan = 6;
  const quads: Quad[] = [];

  for (const [lat, spans] of world.bands) {
    const lo = lat - overlap;
    const hi = lat + world.band + overlap;
    for (let i = 0; i + 1 < spans.length; i += 2) {
      for (let a = spans[i]; a < spans[i + 1]; a += maxSpan) {
        const b = Math.min(spans[i + 1], a + maxSpan);
        quads.push([unit(lo, a), unit(lo, b), unit(hi, b), unit(hi, a)]);
      }
    }
  }
  return quads;
};

/** Graticule polylines — parallels and meridians every 30°. */
const GRATICULE: Vec[][] = (() => {
  const lines: Vec[][] = [];
  for (let lat = -60; lat <= 60; lat += 30) {
    const line: Vec[] = [];
    for (let lon = -180; lon <= 180; lon += 4) line.push(unit(lat, lon));
    lines.push(line);
  }
  for (let lon = -180; lon < 180; lon += 30) {
    const line: Vec[] = [];
    for (let lat = -90; lat <= 90; lat += 4) line.push(unit(lat, lon));
    lines.push(line);
  }
  return lines;
})();

/**
 * Surface mesh, spread with a Fibonacci sphere (no clustering at the poles,
 * unlike a lat/lon grid). Links come from the fixed 3D positions, so which
 * node neighbours which never changes as the globe turns.
 */
const MESH = (() => {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const nodes: Vec[] = [];

  for (let i = 0; i < MESH_NODES; i++) {
    const y = 1 - (i / (MESH_NODES - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * i;
    nodes.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }

  const cos = Math.cos(MESH_LINK_ARC);
  const links: Array<[number, number]> = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dot = nodes[i].x * nodes[j].x + nodes[i].y * nodes[j].y + nodes[i].z * nodes[j].z;
      if (dot > cos) links.push([i, j]);
    }
  }

  return { nodes, links };
})();

type OrbitNode = { Icon: typeof Ticket; label: string };

type Orbit = {
  id: string;
  /** Circle radius in stage units. */
  radius: number;
  /** Tilt of the orbital plane away from face-on, in degrees. Shallow values
   *  give a flat ellipse and carry nodes in front of and behind the globe. */
  inclination: number;
  /** In-screen rotation of the projected ellipse, so the rings criss-cross. */
  swivel: number;
  /** Seconds for one full lap. */
  duration: number;
  /** Reverses travel so neighbouring orbits don't move in lockstep. */
  reverse: boolean;
  nodes: OrbitNode[];
};

const ORBITS: Orbit[] = [
  {
    id: 'a',
    radius: 360,
    inclination: 24,
    swivel: -14,
    duration: 20,
    reverse: false,
    nodes: [
      { Icon: Ticket, label: 'Tickets' },
      { Icon: Webhook, label: 'Webhooks' },
      { Icon: Code2, label: 'API' },
    ],
  },
  {
    id: 'b',
    radius: 424,
    inclination: 33,
    swivel: 22,
    duration: 28,
    reverse: true,
    nodes: [
      { Icon: Bot, label: 'AI' },
      { Icon: Users, label: 'Customers' },
      { Icon: Headphones, label: 'Agents' },
    ],
  },
  {
    id: 'c',
    radius: 472,
    inclination: 19,
    swivel: -36,
    duration: 36,
    reverse: false,
    nodes: [
      { Icon: Workflow, label: 'Automation' },
      { Icon: BarChart3, label: 'Analytics' },
    ],
  },
];

/**
 * Point on an orbit at angle `a`, projected. `depth` is +ve toward the viewer.
 * `dSwivel` / `dIncl` let the pointer lean the whole ring with the globe, so
 * the scene tilts as one body instead of a sphere turning inside static hoops.
 */
const orbitPoint = (o: Orbit, a: number, dSwivel = 0, dIncl = 0) => {
  const i = (o.inclination + dIncl) * RAD;
  const t = (o.swivel + dSwivel) * RAD;
  const x = o.radius * Math.cos(a);
  const z = o.radius * Math.sin(a);
  const y = -z * Math.sin(i);
  const depth = z * Math.cos(i);
  return {
    x: C + x * Math.cos(t) - y * Math.sin(t),
    y: C + x * Math.sin(t) + y * Math.cos(t),
    depth,
  };
};

export function AuthGlobe() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodeRefs = useRef<Array<HTMLDivElement | null>>([]);
  const hovered = useRef<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let spin = 0;
    // Read once per frame and shared with drawRing, so the globe follows the
    // theme toggle without subscribing to the store.
    let dark = true;
    const angles = ORBITS.map(() => 0);
    let frameId = 0;
    let lastTs = 0;

    // Rotation is shared by every point on the sphere, so its sines and
    // cosines are computed once per frame rather than per point.
    let cosS = 1;
    let sinS = 0;
    let cosT = Math.cos(AXIAL_TILT * RAD);
    let sinT = Math.sin(AXIAL_TILT * RAD);

    // Pointer lean and scroll spin. Both are eased toward their target rather
    // than applied raw, so the globe follows the cursor instead of snapping to
    // it, and a flick of the wheel coasts to a stop.
    let yaw = 0;
    let pitch = 0;
    let targetYaw = 0;
    let targetPitch = 0;
    let scrollVel = 0;

    const px = { x: 0, y: 0, z: 0 };

    // Populated once the world data lands. Until then the globe renders with
    // its ocean, graticule and mesh only — the form never waits on this.
    let outlines: Vec[][] = [];
    let landQuads: Quad[] = [];
    // Scratch buffer for a quad's projected corners, reused every frame.
    const corners = new Float64Array(8);

    /** Rotate a cached unit vector into view. Writes into `px` to avoid garbage. */
    const project = (v: Vec) => {
      const x = v.x * cosS + v.z * sinS;
      const z1 = -v.x * sinS + v.z * cosS;
      px.x = C + x * GLOBE_R;
      px.y = C + (v.y * cosT - z1 * sinT) * GLOBE_R;
      px.z = v.y * sinT + z1 * cosT;
      return px;
    };

    /** Map the canvas backing store onto the BOX-unit space, at device DPI. */
    const fit = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const size = Math.round(rect.width * dpr);
      canvas.width = size;
      canvas.height = size;
      const k = size / BOX;
      ctx.setTransform(k, 0, 0, k, 0, 0);
    };

    /** One orbit ring, drawn only where it is in front of or behind the globe. */
    const drawRing = (o: Orbit, front: boolean) => {
      ctx.strokeStyle = dark
        ? front
          ? 'rgba(96,165,250,0.42)'
          : 'rgba(96,165,250,0.16)'
        : front
          ? 'rgba(37,99,235,0.45)'
          : 'rgba(37,99,235,0.18)';
      ctx.lineWidth = front ? 1.1 : 0.9;
      ctx.beginPath();
      let open = false;
      for (let s = 0; s <= 160; s++) {
        const p = orbitPoint(o, (s / 160) * Math.PI * 2, yaw * 0.25, pitch * 0.5);
        if (front ? p.depth < 0 : p.depth >= 0) {
          open = false;
          continue;
        }
        if (open) ctx.lineTo(p.x, p.y);
        else {
          ctx.moveTo(p.x, p.y);
          open = true;
        }
      }
      ctx.stroke();
    };

    const draw = () => {
      if (canvas.width === 0) return;
      dark = document.documentElement.classList.contains('dark');
      ctx.clearRect(0, 0, BOX, BOX);

      const rot = (spin + yaw) * RAD;
      cosS = Math.cos(rot);
      sinS = Math.sin(rot);
      const tilt = (AXIAL_TILT + pitch) * RAD;
      cosT = Math.cos(tilt);
      sinT = Math.sin(tilt);

      // ── Backdrop pattern ────────────────────────────────────────────────
      // Concentric dashed rings, a slow dial of radial ticks and a pair of
      // counter-rotating polygons. Drawn first, so everything else sits over it.
      const line = dark ? '96,165,250' : '37,99,235';
      const slow = spin * 0.35 * RAD;

      ctx.setLineDash([3, 9]);
      ctx.lineWidth = 1;
      for (const k of [1.22, 1.44, 1.68]) {
        ctx.strokeStyle = `rgba(${line},${dark ? 0.16 : 0.14})`;
        ctx.beginPath();
        ctx.arc(C, C, GLOBE_R * k, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      ctx.strokeStyle = `rgba(${line},${dark ? 0.22 : 0.2})`;
      ctx.beginPath();
      for (let a = 0; a < 360; a += 6) {
        const rad = a * RAD + slow;
        const len = a % 30 === 0 ? 18 : 9;
        const r0 = GLOBE_R * 1.74;
        ctx.moveTo(C + Math.cos(rad) * r0, C + Math.sin(rad) * r0);
        ctx.lineTo(C + Math.cos(rad) * (r0 + len), C + Math.sin(rad) * (r0 + len));
      }
      ctx.stroke();

      for (const [sides, k, dir] of [
        [6, 1.95, 1],
        [3, 1.34, -1],
      ] as Array<[number, number, number]>) {
        ctx.strokeStyle = `rgba(${line},${dark ? 0.14 : 0.12})`;
        ctx.beginPath();
        for (let i = 0; i <= sides; i++) {
          const rad = (i / sides) * Math.PI * 2 - Math.PI / 2 + dir * slow * 0.6;
          const x = C + Math.cos(rad) * GLOBE_R * k;
          const y = C + Math.sin(rad) * GLOBE_R * k;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Halo.
      const halo = ctx.createRadialGradient(C, C, GLOBE_R * 0.9, C, C, GLOBE_R * 1.5);
      halo.addColorStop(0, dark ? 'rgba(59,130,246,0.2)' : 'rgba(37,99,235,0.16)');
      halo.addColorStop(1, 'rgba(37,99,235,0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R * 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Orbit rings, far half first — the sphere is painted over them next.
      for (const o of ORBITS) drawRing(o, false);

      // ── Sphere surface ───────────────────────────────────────────────────
      // The globe is hollow: the far wall is drawn first and dimmed, then a
      // faint interior haze stands in for the glass volume, then the near wall
      // on top. Depth reads from that contrast rather than from occlusion.
      ctx.save();
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R, 0, Math.PI * 2);
      ctx.clip();

      /**
       * Filled land for one hemisphere, as a single path so overlapping quads
       * union cleanly instead of showing seams. Quads straddling the limb are
       * skipped — their projection folds — and the outline covers the gap.
       */
      const drawLand = (near: boolean, style: string) => {
        if (!landQuads.length) return;
        ctx.fillStyle = style;
        ctx.beginPath();
        for (const quad of landQuads) {
          let ok = true;
          for (let i = 0; i < 4; i++) {
            const p = project(quad[i]);
            if (p.z > 0 !== near) {
              ok = false;
              break;
            }
            corners[i * 2] = p.x;
            corners[i * 2 + 1] = p.y;
          }
          if (!ok) continue;
          ctx.moveTo(corners[0], corners[1]);
          ctx.lineTo(corners[2], corners[3]);
          ctx.lineTo(corners[4], corners[5]);
          ctx.lineTo(corners[6], corners[7]);
          ctx.closePath();
        }
        ctx.fill();
      };

      /**
       * Any set of polylines on the sphere, for one hemisphere. Used for both
       * the coastlines/borders and the graticule: a line is broken wherever it
       * crosses the horizon, so nothing wraps around the far side.
       */
      const drawPolylines = (lines: Vec[][], near: boolean, style: string, width: number) => {
        if (!lines.length) return;
        ctx.strokeStyle = style;
        ctx.lineWidth = width;
        for (const line of lines) {
          ctx.beginPath();
          let open = false;
          for (const v of line) {
            const p = project(v);
            if (p.z > 0 !== near) {
              open = false;
              continue;
            }
            if (open) ctx.lineTo(p.x, p.y);
            else {
              ctx.moveTo(p.x, p.y);
              open = true;
            }
          }
          ctx.stroke();
        }
      };

      // Far wall, seen through the globe.
      drawPolylines(GRATICULE, false, dark ? 'rgba(147,197,253,0.12)' : 'rgba(147,197,253,0.16)', 0.7);
      drawLand(false, dark ? 'rgba(96,165,250,0.26)' : 'rgba(37,99,235,0.22)');
      drawPolylines(outlines, false, dark ? 'rgba(147,197,253,0.3)' : 'rgba(147,197,253,0.4)', 0.9);

      // Interior haze — the glass volume between the two walls. Thin in the
      // middle where you look straight through, denser toward the limb where
      // the line of sight passes through more of it.
      const haze = ctx.createRadialGradient(C, C, GLOBE_R * 0.1, C, C, GLOBE_R);
      haze.addColorStop(0, dark ? 'rgba(29,78,216,0.06)' : 'rgba(59,130,246,0.05)');
      haze.addColorStop(0.75, dark ? 'rgba(21,42,99,0.13)' : 'rgba(37,99,235,0.1)');
      haze.addColorStop(1, dark ? 'rgba(7,13,28,0.3)' : 'rgba(12,26,61,0.2)');
      ctx.fillStyle = haze;
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R, 0, Math.PI * 2);
      ctx.fill();

      // Near wall.
      drawPolylines(GRATICULE, true, dark ? 'rgba(147,197,253,0.28)' : 'rgba(191,219,254,0.36)', 0.7);
      drawLand(true, dark ? 'rgba(147,197,253,0.55)' : 'rgba(37,99,235,0.5)');
      drawPolylines(outlines, true, dark ? 'rgba(219,234,254,0.8)' : 'rgba(255,255,255,0.9)', 1);

      // Surface mesh, near side only — mirroring it through the glass reads as
      // clutter rather than depth.
      const mesh = dark ? '147,197,253' : '191,219,254';
      const projected = MESH.nodes.map((n) => {
        const p = project(n);
        return p.z > 0.02 ? { x: p.x, y: p.y, z: p.z } : null;
      });

      ctx.lineWidth = 0.6;
      for (const [i, j] of MESH.links) {
        const a = projected[i];
        const b = projected[j];
        if (!a || !b) continue;
        ctx.strokeStyle = `rgba(${mesh},${(Math.min(a.z, b.z) * 0.42).toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      for (const p of projected) {
        if (!p) continue;
        ctx.fillStyle = `rgba(${mesh},${(p.z * 0.68).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }

      // Edge shading only. A solid planet gets a terminator; a hollow glass
      // sphere just darkens where the wall is edge-on.
      const edge = ctx.createRadialGradient(C, C, GLOBE_R * 0.72, C, C, GLOBE_R);
      edge.addColorStop(0, 'rgba(2,6,23,0)');
      edge.addColorStop(1, dark ? 'rgba(2,6,23,0.22)' : 'rgba(2,6,23,0.14)');
      ctx.fillStyle = edge;
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();

      // ── Glass shell ──────────────────────────────────────────────────────
      // Its refraction edge is what sells "glass": bright where the shell is
      // seen at a glancing angle, invisible where you look straight through.
      const shellR = GLOBE_R * 1.085;
      const shell = ctx.createRadialGradient(C, C, GLOBE_R * 0.82, C, C, shellR);
      shell.addColorStop(0, 'rgba(255,255,255,0)');
      shell.addColorStop(0.82, dark ? 'rgba(147,197,253,0.06)' : 'rgba(255,255,255,0.06)');
      shell.addColorStop(0.955, dark ? 'rgba(191,219,254,0.6)' : 'rgba(255,255,255,0.72)');
      shell.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = shell;
      ctx.beginPath();
      ctx.arc(C, C, shellR, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = dark ? 'rgba(147,197,253,0.34)' : 'rgba(255,255,255,0.45)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(C, C, shellR, 0, Math.PI * 2);
      ctx.stroke();

      // Atmosphere: a bright glowing band along the upper limb. shadowBlur is
      // the cheap way to get a real bloom here — a gradient can't follow an arc.
      ctx.save();
      ctx.shadowColor = dark ? 'rgba(96,165,250,0.95)' : 'rgba(37,99,235,0.8)';
      ctx.shadowBlur = GLOBE_R * 0.11;
      ctx.strokeStyle = dark ? 'rgba(219,234,254,0.9)' : 'rgba(255,255,255,0.95)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R * 1.005, Math.PI * 1.06, Math.PI * 1.94);
      ctx.stroke();
      ctx.stroke();
      ctx.restore();

      // Bounce light along the lower-right limb.
      ctx.strokeStyle = dark ? 'rgba(191,219,254,0.58)' : 'rgba(255,255,255,0.72)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(C, C, GLOBE_R * 1.02, 0.1 * Math.PI, 0.62 * Math.PI);
      ctx.stroke();

      // Orbit rings, near half — painted over the sphere to close the loop.
      for (const o of ORBITS) drawRing(o, true);
    };

    /** Place the HTML nodes from the same 3D maths that drew the rings. */
    const placeNodes = () => {
      let n = 0;
      ORBITS.forEach((o, oi) => {
        o.nodes.forEach((_, i) => {
          const el = nodeRefs.current[n++];
          if (!el) return;

          const phase = (i / o.nodes.length) * Math.PI * 2;
          const dir = o.reverse ? -1 : 1;
          const p = orbitPoint(o, phase + dir * angles[oi], yaw * 0.25, pitch * 0.5);

          // Behind the sphere and inside its silhouette: occluded.
          const dx = p.x - C;
          const dy = p.y - C;
          const behind = p.depth < 0;
          const occluded = behind && Math.hypot(dx, dy) < GLOBE_R * 1.02;

          const scale = 0.78 + 0.22 * ((p.depth / o.radius + 1) / 2);
          el.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%) scale(${scale.toFixed(3)})`;
          el.style.opacity = occluded ? '0' : behind ? '0.5' : '1';
          el.style.pointerEvents = occluded ? 'none' : 'auto';
          el.style.zIndex = behind ? '1' : '3';
        });
      });
    };

    const motionOff =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.documentElement.classList.contains('a11y-reduced-motion');

    const onResize = () => {
      fit();
      draw();
    };

    // Cursor position maps to a lean; the globe turns toward the pointer.
    const onPointerMove = (e: PointerEvent) => {
      targetYaw = (e.clientX / window.innerWidth - 0.5) * 52;
      targetPitch = -(e.clientY / window.innerHeight - 0.5) * 24;
    };
    // A wheel flick spins the globe the way the page would have moved.
    const onWheel = (e: WheelEvent) => {
      scrollVel += e.deltaY * 0.02;
    };

    fit();
    draw();
    placeNodes();
    window.addEventListener('resize', onResize);

    /*
     * Load the world after first paint. It is fetched rather than imported so
     * it never enters the JS bundle or delays the sign-in form, and a failure
     * is silent by design — the globe simply keeps its ocean and graticule.
     */
    const geo = new AbortController();
    fetch(WORLD_URL, { signal: geo.signal })
      .then((r) => (r.ok ? (r.json() as Promise<World>) : Promise.reject(r.status)))
      .then((world) => {
        outlines = buildOutlines(world);
        landQuads = buildLandQuads(world);
        draw();
      })
      .catch(() => {
        /* Decorative: a missing map must never surface as an error. */
      });

    if (!motionOff) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('wheel', onWheel, { passive: true });

      const loop = (ts: number) => {
        const dt = lastTs ? Math.min(ts - lastTs, 48) : 16;
        lastTs = ts;
        const k = Math.min(1, dt / 16);

        // Ease toward the pointer, and let the scroll impulse decay.
        yaw += (targetYaw - yaw) * 0.1 * k;
        pitch += (targetPitch - pitch) * 0.1 * k;
        spin += scrollVel * k;
        scrollVel *= Math.pow(0.92, k);
        if (Math.abs(scrollVel) < 0.001) scrollVel = 0;

        spin = (spin + (dt / 1000) * SPIN_PER_SEC) % 360;
        ORBITS.forEach((o, i) => {
          if (hovered.current === o.id) return;
          angles[i] = (angles[i] + ((dt / 1000) / o.duration) * Math.PI * 2) % (Math.PI * 2);
        });
        draw();
        placeNodes();
        frameId = requestAnimationFrame(loop);
      };
      frameId = requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('wheel', onWheel);
      geo.abort();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, []);

  let index = 0;

  return (
    <div className="auth-stage-wrap">
      {/* .auth-stage takes the scaled footprint; .auth-stage-inner holds the
          unscaled 1000×1000 coordinate space everything is authored against. */}
      <div className="auth-stage">
        <div className="auth-stage-inner">
          <canvas
            ref={canvasRef}
            className="auth-globe-canvas"
            role="img"
            aria-label="A rotating globe with tickets, webhooks, API, AI, customers, agents, automation and analytics in orbit around it"
          />

          {ORBITS.map((o) =>
            o.nodes.map((n) => {
              const at = index++;
              return (
                <div
                  key={n.label}
                  ref={(el) => {
                    nodeRefs.current[at] = el;
                  }}
                  className="auth-node"
                  onPointerEnter={() => {
                    hovered.current = o.id;
                  }}
                  onPointerLeave={() => {
                    hovered.current = null;
                  }}
                >
                  <span className="auth-node-card">
                    <n.Icon width={18} height={18} strokeWidth={1.9} />
                    <span>{n.label}</span>
                  </span>
                </div>
              );
            }),
          )}
        </div>
      </div>
    </div>
  );
}
